import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import styles from './drawer.module.scss';
import { Icon } from '../icon';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  preventCloseOnOverlayClick?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  className,
  preventCloseOnOverlayClick = false
}) => {
  const [shouldRender, setRender] = useState(open);
  const [isOpening, setIsOpening] = useState(open);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let rAF: number;

    if (open) {
      setRender(true);
      document.body.style.overflow = 'hidden';
      rAF = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => setIsOpening(true), 10);
      });
    } else {
      setIsOpening(false);
      document.body.style.overflow = '';
      timeoutId = setTimeout(() => setRender(false), 300);
    }

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rAF);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!shouldRender) return null;

  return createPortal(
    <div 
      className={styles.overlay} 
      onClick={(e) => {
        if (!preventCloseOnOverlayClick && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className={classNames(styles.drawer, className, {
          [styles.isOpen]: isOpening
        })}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.drawerHeader}>
          {title}
          <button 
            type="button" 
            className="button is-small" 
            style={{ border: 'none', background: 'transparent' }} 
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className={styles.drawerContainer}>
          {children}
        </div>
        {footer && (
          <div className={styles.drawerFooter}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
